import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatPhoneNumber(phone: string): string {
  // E.164形式の電話番号を表示用にフォーマット
  if (phone.startsWith('+81')) {
    return phone.replace(/^\+81/, '0').replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3')
  }
  return phone
}

export function parsePhoneNumber(phone: string): string {
  // 表示用の電話番号をE.164形式に変換
  const cleaned = phone.replace(/[^\d]/g, '')
  if (cleaned.startsWith('0')) {
    return '+81' + cleaned.substring(1)
  }
  if (cleaned.startsWith('81')) {
    return '+' + cleaned
  }
  return '+' + cleaned
}

export function generateBookingNo(): string {
  const now = new Date()
  const year = now.getFullYear().toString().slice(-2)
  const month = (now.getMonth() + 1).toString().padStart(2, '0')
  const day = now.getDate().toString().padStart(2, '0')
  const random = Math.random().toString(36).substring(2, 8).toUpperCase()
  return `${year}${month}${day}${random}`
}

export function formatDate(date: string | Date, format: 'short' | 'long' = 'short'): string {
  const d = new Date(date)
  const options: Intl.DateTimeFormatOptions = {
    timeZone: 'Asia/Tokyo',
    ...(format === 'short'
      ? { year: 'numeric', month: '2-digit', day: '2-digit' }
      : { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' }
    )
  }
  return d.toLocaleDateString('ja-JP', options)
}

export function formatDateTime(date: string | Date): string {
  const d = new Date(date)
  const options: Intl.DateTimeFormatOptions = {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  }
  return d.toLocaleString('ja-JP', options)
}

export function formatTime(date: string | Date, use24HourExtended = false): string {
  const d = new Date(date)

  if (use24HourExtended) {
    // 24時間を超える表示（24:00, 25:00等）をサポート
    // 基準日からの経過時間を計算して24時間制表示を生成
    return formatTimeExtended(d)
  }

  const options: Intl.DateTimeFormatOptions = {
    timeZone: 'Asia/Tokyo',
    hour: '2-digit',
    minute: '2-digit'
  }
  return d.toLocaleTimeString('ja-JP', options)
}

// 給与・利益計算（共通ユーティリティ）
export function calculatePayoutAndProfit(params: {
  price: number | null | undefined
  payoutRatePercent: number | null | undefined
}): { castPayout: number; shopProfit: number } {
  const price = typeof params.price === 'number' && isFinite(params.price) ? Math.max(0, Math.floor(params.price)) : 0
  const rate = typeof params.payoutRatePercent === 'number' && isFinite(params.payoutRatePercent)
    ? Math.min(100, Math.max(0, params.payoutRatePercent))
    : 0
  const castPayout = Math.round(price * (rate / 100))
  const shopProfit = price - castPayout
  return { castPayout, shopProfit }
}

// 端数処理付きのギャラ計算（基本ユーティリティ）
export type RoundingMode = 'floor' | 'round' | 'ceil'

export function computeRoundedAmount(amount: number, mode: RoundingMode, unit: 1 | 10 | 100): number {
  const safeAmount = typeof amount === 'number' && isFinite(amount) ? amount : 0
  const u = unit as number
  if (u <= 0) return Math.round(safeAmount)
  const divided = safeAmount / u
  const rounded = mode === 'floor' ? Math.floor(divided) : mode === 'ceil' ? Math.ceil(divided) : Math.round(divided)
  return rounded * u
}

export function computePayoutAmount(params: {
  price: number | null | undefined
  payoutRatePercent: number | null | undefined
  roundingMode: RoundingMode
  roundingUnit: 1 | 10 | 100
}): number {
  const price = typeof params.price === 'number' && isFinite(params.price) ? Math.max(0, Math.floor(params.price)) : 0
  const rate = typeof params.payoutRatePercent === 'number' && isFinite(params.payoutRatePercent)
    ? Math.min(100, Math.max(0, params.payoutRatePercent))
    : 0
  const raw = price * (rate / 100)
  return computeRoundedAmount(raw, params.roundingMode, params.roundingUnit)
}

export function computeDualPayout(params: {
  totalPrice: number | null | undefined
  optionPrice: number | null | undefined
  baseRatePercent: number | null | undefined
  optionRatePercent: number | null | undefined
  roundingMode: RoundingMode
  roundingUnit: 1 | 10 | 100
}): { basePrice: number; optionPrice: number; basePayout: number; optionPayout: number; totalPayout: number } {
  const total = typeof params.totalPrice === 'number' && isFinite(params.totalPrice) ? Math.max(0, Math.floor(params.totalPrice)) : 0
  const opt = typeof params.optionPrice === 'number' && isFinite(params.optionPrice) ? Math.max(0, Math.floor(params.optionPrice)) : 0
  const basePrice = Math.max(0, total - opt)
  const basePayout = computePayoutAmount({
    price: basePrice,
    payoutRatePercent: params.baseRatePercent,
    roundingMode: params.roundingMode,
    roundingUnit: params.roundingUnit
  })
  const optionPayout = computePayoutAmount({
    price: opt,
    payoutRatePercent: params.optionRatePercent,
    roundingMode: params.roundingMode,
    roundingUnit: params.roundingUnit
  })
  return { basePrice, optionPrice: opt, basePayout, optionPayout, totalPayout: basePayout + optionPayout }
}

// 24時間を超える時間表示をサポートする関数
export function formatTimeExtended(date: Date, baseDate?: Date): string {
  const targetDate = new Date(date)
  const base = baseDate || new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate())

  // 基準日からの経過分を計算
  const diffMs = targetDate.getTime() - base.getTime()
  const totalMinutes = Math.floor(diffMs / (1000 * 60))

  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60

  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
}

// 拡張された時間文字列（24:00, 25:00等）を分に変換する関数
export function timeToMinutesExtended(time: string): number {
  const [hours, minutes] = time.split(':').map(Number)
  return hours * 60 + minutes
}

// 分から拡張された時間文字列（24:00, 25:00等）に変換する関数  
export function minutesToTimeExtended(minutes: number): string {
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`
}

// キャストごとの色を生成する関数
export function getCastColor(castId: string, castName?: string): {
  background: string;
  backgroundHover: string;
  border: string;
  text: string;
  lightBackground: string;
} {
  // デバッグ用：キャストIDと名前をコンソールに出力
  console.log('getCastColor called with castId:', castId, 'castName:', castName)

  // キャスト名に基づいた色分け（暖色と寒色を交互にバランスよく配置）
  if (castName) {
    const nameHash = castName.charCodeAt(0) % 12
    const colorPalette = [
      // 1. 赤色（暖色）
      {
        background: 'bg-red-50',
        backgroundHover: 'hover:bg-red-100',
        border: 'border-red-600',
        text: 'text-red-800',
        lightBackground: 'bg-red-25'
      },
      // 2. 青色（寒色）
      {
        background: 'bg-blue-50',
        backgroundHover: 'hover:bg-blue-100',
        border: 'border-blue-600',
        text: 'text-blue-800',
        lightBackground: 'bg-blue-25'
      },
      // 3. オレンジ色（暖色）
      {
        background: 'bg-orange-50',
        backgroundHover: 'hover:bg-orange-100',
        border: 'border-orange-600',
        text: 'text-orange-800',
        lightBackground: 'bg-orange-25'
      },
      // 4. 緑色（寒色）
      {
        background: 'bg-green-50',
        backgroundHover: 'hover:bg-green-100',
        border: 'border-green-600',
        text: 'text-green-800',
        lightBackground: 'bg-green-25'
      },
      // 5. ピンク色（暖色）
      {
        background: 'bg-pink-50',
        backgroundHover: 'hover:bg-pink-100',
        border: 'border-pink-600',
        text: 'text-pink-800',
        lightBackground: 'bg-pink-25'
      },
      // 6. 紫色（寒色）
      {
        background: 'bg-purple-50',
        backgroundHover: 'hover:bg-purple-100',
        border: 'border-purple-600',
        text: 'text-purple-800',
        lightBackground: 'bg-purple-25'
      },
      // 7. アンバー色（暖色）
      {
        background: 'bg-amber-50',
        backgroundHover: 'hover:bg-amber-100',
        border: 'border-amber-600',
        text: 'text-amber-800',
        lightBackground: 'bg-amber-25'
      },
      // 8. ティール色（寒色）
      {
        background: 'bg-teal-50',
        backgroundHover: 'hover:bg-teal-100',
        border: 'border-teal-600',
        text: 'text-teal-800',
        lightBackground: 'bg-teal-25'
      },
      // 9. ローズ色（暖色）
      {
        background: 'bg-rose-50',
        backgroundHover: 'hover:bg-rose-100',
        border: 'border-rose-600',
        text: 'text-rose-800',
        lightBackground: 'bg-rose-25'
      },
      // 10. インディゴ色（寒色）
      {
        background: 'bg-indigo-50',
        backgroundHover: 'hover:bg-indigo-100',
        border: 'border-indigo-600',
        text: 'text-indigo-800',
        lightBackground: 'bg-indigo-25'
      },
      // 11. 黄色（暖色）
      {
        background: 'bg-yellow-50',
        backgroundHover: 'hover:bg-yellow-100',
        border: 'border-yellow-600',
        text: 'text-yellow-800',
        lightBackground: 'bg-yellow-25'
      },
      // 12. シアン色（寒色）
      {
        background: 'bg-cyan-50',
        backgroundHover: 'hover:bg-cyan-100',
        border: 'border-cyan-600',
        text: 'text-cyan-800',
        lightBackground: 'bg-cyan-25'
      }
    ]

    const selectedColor = colorPalette[nameHash]
    console.log('Selected color for castName', castName, ':', selectedColor)
    return selectedColor
  }

  // フォールバック：キャストIDからハッシュ値を生成
  let hash = 0
  for (let i = 0; i < castId.length; i++) {
    const char = castId.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash // 32bit整数に変換
  }

  // ハッシュ値を正の数に変換
  hash = Math.abs(hash)

  // 事前定義された色パレット（暖色と寒色を交互にバランスよく配置）
  const colorPalette = [
    // 1. 赤色（暖色）
    {
      background: 'bg-red-50',
      backgroundHover: 'hover:bg-red-100',
      border: 'border-red-600',
      text: 'text-red-800',
      lightBackground: 'bg-red-25'
    },
    // 2. 青色（寒色）
    {
      background: 'bg-blue-50',
      backgroundHover: 'hover:bg-blue-100',
      border: 'border-blue-600',
      text: 'text-blue-800',
      lightBackground: 'bg-blue-25'
    },
    // 3. オレンジ色（暖色）
    {
      background: 'bg-orange-50',
      backgroundHover: 'hover:bg-orange-100',
      border: 'border-orange-600',
      text: 'text-orange-800',
      lightBackground: 'bg-orange-25'
    },
    // 4. 緑色（寒色）
    {
      background: 'bg-green-50',
      backgroundHover: 'hover:bg-green-100',
      border: 'border-green-600',
      text: 'text-green-800',
      lightBackground: 'bg-green-25'
    },
    // 5. ピンク色（暖色）
    {
      background: 'bg-pink-50',
      backgroundHover: 'hover:bg-pink-100',
      border: 'border-pink-600',
      text: 'text-pink-800',
      lightBackground: 'bg-pink-25'
    },
    // 6. 紫色（寒色）
    {
      background: 'bg-purple-50',
      backgroundHover: 'hover:bg-purple-100',
      border: 'border-purple-600',
      text: 'text-purple-800',
      lightBackground: 'bg-purple-25'
    },
    // 7. アンバー色（暖色）
    {
      background: 'bg-amber-50',
      backgroundHover: 'hover:bg-amber-100',
      border: 'border-amber-600',
      text: 'text-amber-800',
      lightBackground: 'bg-amber-25'
    },
    // 8. ティール色（寒色）
    {
      background: 'bg-teal-50',
      backgroundHover: 'hover:bg-teal-100',
      border: 'border-teal-600',
      text: 'text-teal-800',
      lightBackground: 'bg-teal-25'
    },
    // 9. ローズ色（暖色）
    {
      background: 'bg-rose-50',
      backgroundHover: 'hover:bg-rose-100',
      border: 'border-rose-600',
      text: 'text-rose-800',
      lightBackground: 'bg-rose-25'
    },
    // 10. インディゴ色（寒色）
    {
      background: 'bg-indigo-50',
      backgroundHover: 'hover:bg-indigo-100',
      border: 'border-indigo-600',
      text: 'text-indigo-800',
      lightBackground: 'bg-indigo-25'
    },
    // 11. 黄色（暖色）
    {
      background: 'bg-yellow-50',
      backgroundHover: 'hover:bg-yellow-100',
      border: 'border-yellow-600',
      text: 'text-yellow-800',
      lightBackground: 'bg-yellow-25'
    },
    // 12. シアン色（寒色）
    {
      background: 'bg-cyan-50',
      backgroundHover: 'hover:bg-cyan-100',
      border: 'border-cyan-600',
      text: 'text-cyan-800',
      lightBackground: 'bg-cyan-25'
    }
  ]

  // ハッシュ値に基づいて色を選択
  const colorIndex = hash % colorPalette.length
  const selectedColor = colorPalette[colorIndex]

  // デバッグ用：選択された色をコンソールに出力
  console.log('Selected color for castId', castId, ':', selectedColor)

  return selectedColor
}

// 予約ブロック用の高彩度・高コントラストカラーを生成する関数
export function getBookingColors(status: 'pending' | 'confirmed' | 'cancelled' | 'complete'): {
  background: string;
  border: string;
  text: string;
  hover: string;
} {
  switch (status) {
    case 'pending':
      return {
        background: 'bg-orange-500',
        border: 'border-orange-600',
        text: 'text-white',
        hover: 'hover:bg-orange-600'
      }
    case 'confirmed':
      return {
        background: 'bg-green-500',
        border: 'border-green-600',
        text: 'text-white',
        hover: 'hover:bg-green-600'
      }
    case 'cancelled':
      return {
        background: 'bg-red-500',
        border: 'border-red-600',
        text: 'text-white',
        hover: 'hover:bg-red-600'
      }
    case 'complete':
      return {
        background: 'bg-blue-500',
        border: 'border-blue-600',
        text: 'text-white',
        hover: 'hover:bg-blue-600'
      }
    default:
      return {
        background: 'bg-gray-500',
        border: 'border-gray-600',
        text: 'text-white',
        hover: 'hover:bg-gray-600'
      }
  }
}
